import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import api, { inr } from "../api";
import PropertyForm from "./PropertyForm";

export default function MyProperties() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/my/properties");
      setItems(data.properties);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const onSaved = () => { setShowForm(false); setEditing(null); load(); toTop(); };
  const del = async (id) => {
    if (!window.confirm("Delete this property?")) return;
    await api.delete(`/properties/${id}`);
    load();
  };

  if (showForm)
    return (
      <div className="bg-surface border border-neutral-200 rounded-2xl p-6 md:p-8 shadow-card" data-testid="property-form-wrap">
        <h2 className="text-2xl font-bold text-neutral-900 mb-6">{editing ? "Edit Property" : "List a New Property"}</h2>
        <PropertyForm initial={editing} onSaved={onSaved} onCancel={() => { setShowForm(false); setEditing(null); }} />
      </div>
    );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-neutral-900">My Listings <span className="text-neutral-400 text-lg">({items.length})</span></h2>
        <button onClick={() => { setEditing(null); setShowForm(true); toTop(); }} data-testid="add-property-btn"
          className="flex items-center gap-2 px-5 py-2.5 font-semibold text-white bg-gradient-to-r from-brand-500 to-brand-600 rounded-xl hover:shadow-soft transition-all active:scale-95">
          <Plus className="w-5 h-5" /> Add Property
        </button>
      </div>

      {loading ? (
        <p className="text-neutral-500 py-10 text-center">Loading…</p>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-neutral-300 rounded-2xl" data-testid="no-listings">
          <p className="text-neutral-500">You haven't listed any property yet.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {items.map((p) => (
            <div key={p.id} className="flex flex-col sm:flex-row gap-4 bg-surface border border-neutral-200 rounded-2xl p-4 shadow-card" data-testid={`my-property-${p.id}`}>
              <img src={p.image} alt={p.title} className="w-full sm:w-40 h-28 object-cover rounded-xl" />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-brand-500 text-white">{p.listing_type}</span>
                  <span className="text-xs text-neutral-500">{p.property_type}</span>
                </div>
                <Link to={`/properties/${p.id}`} className="font-semibold text-neutral-900 hover:text-brand-600">{p.title}</Link>
                <p className="flex items-center gap-1 text-neutral-500 text-sm mt-1"><MapPin className="w-3.5 h-3.5" /> {p.area}, {p.city}</p>
                <p className="text-brand-600 font-bold mt-1">{inr(p.price)}</p>
              </div>
              <div className="flex sm:flex-col gap-2">
                <button onClick={() => { setEditing(p); setShowForm(true); toTop(); }} data-testid={`edit-property-${p.id}`}
                  className="flex items-center gap-1 px-3 py-2 text-sm border border-neutral-300 rounded-lg text-neutral-700 hover:text-neutral-900">
                  <Pencil className="w-4 h-4" /> Edit
                </button>
                <button onClick={() => del(p.id)} data-testid={`delete-property-${p.id}`}
                  className="flex items-center gap-1 px-3 py-2 text-sm border border-red-300 rounded-lg text-red-500 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
